/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as GooglePlaces from './googlePlaces';
import { diagnostics } from '@utils/diagnostics';
import { extend, includes, size } from '@utils/lodash';

// https://developers.google.com/maps/documentation/javascript/places-autocomplete#session_tokens,
// Google Places now allows session tokens to group together searches for billing purposes.
// These tokens should be specified and regenerated by components that use this function.
// Tokens should be regenerated each time a Place Details request is made at getFullAddress below.
export const getAutocompleteMatches = function (
  inputText: string,
  sessionToken?: google.maps.places.AutocompleteSessionToken,
  overrideParams?: { types?: string[] },
): Promise<google.maps.places.AutocompletePrediction[]> {
  if (!inputText) {
    return Promise.resolve([]);
  }
  return new Promise<google.maps.places.AutocompletePrediction[]>(function (resolve) {
    const country: string | string[] = GooglePlaces.getGooglePlacesCountryRestriction();
    const params = extend(
      { input: inputText, componentRestrictions: { country }, sessionToken },
      overrideParams,
    ) as any;

    GooglePlaces.autocompleteService().getPlacePredictions(params, function (result, status) {
      // We reject route suggestions because they can produce misleading results in our address parsing
      // e.g. we may say your address line is "Main St." instead of providing a specific number, because in route
      // suggestions no number is given
      if (!result) {
        return resolve([]);
      }
      const nonRouteSuggestions = result.filter(prediction => !includes(prediction.types, 'route'));
      if (size(nonRouteSuggestions) > 0) {
        return resolve(nonRouteSuggestions);
      }
      if (status !== GooglePlaces.PlacesServiceStatus().OK) {
        diagnostics.breadcrumb(`Failed to get addresses from google places: ${status}`);
      }

      return resolve([]);
    });
  });
};

export const googlePlacesEnabled = GooglePlaces.isPlacesLoaded;

export const generateNewSessionToken = GooglePlaces.generateNewSessionToken;

export const getFullAddress = function (
  address: google.maps.places.AutocompletePrediction,
  sessionToken?: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.PlaceResult> {
  return new Promise<google.maps.places.PlaceResult>(function (resolve, reject) {
    GooglePlaces.detailsService().getDetails({ placeId: address.place_id, sessionToken }, function (result, status) {
      if (status !== GooglePlaces.PlacesServiceStatus().OK) {
        // Load in the address they tried to select so it doesn't look like we're dropping information.
        const error = new Error(`Failed to fetch places status: ${status}`);

        /* istanbul ignore next */
        if (!includes(['NOT_FOUND'], status)) {
          diagnostics.error(error, { fingerprint: [status as any] });
        }

        return reject(error);
      }

      resolve(result);
    });
  });
};
